import React, { useRef, useState } from 'react';
import { toast } from '@shared/ui';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import Input, { InputType } from '@zvuk-b2b/react-uikit/ui/Input';
import TextArea from '@zvuk-b2b/react-uikit/ui/TextArea';
import { useNavigate } from 'react-router-dom';
import DraggableVideoMediafiesList from '../DragableVideoMediafiesList';

import styles from './index.module.scss';
import { TEXTS } from '../texts';
import { DATA_TEST_IDS } from '../consts';
import { useCreateVideoStreamController } from './useCreateVideoStreamController';
import { useCreateVideoStreamPresenter } from '../presenters/createVideoStreamPresenter';

const CreateVideoStreamModal: React.FC<ModalProps[ModalTypes.CREATE_VIDEO_STREAM]> = ({
  openModal,
  close,
  selectedFiles,
  emenuMediafiles,
  flow,
  title = '',
  description = '',
  videoStreamId = '',
  isGroupSlave,
  refetch,
}) => {
  const { createVideoStream, updateVideoStream, companyId } = useCreateVideoStreamPresenter();
  const {
    onSave,
    mediafiles,
    mediafilesToSave,
    emenuOrder,
    setMediafiles,
    setEmenuOrder,
    videoStreamTitle,
    setVideoStreamTitle,
    videoStreamDescription,
    setVideoStreamDescription,
    modalTitle,
    setMediafilePlayDuration,
    setMediafileDraftDuration,
    removeMediafile,
    copyMediafile,
  } = useCreateVideoStreamController({
    createVideoStream,
    updateVideoStream,
    selectedFiles,
    emenuMediafiles,
    title,
    description,
    flow,
    videoStreamId,
    companyId,
  });

  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [savedStreamId, setSavedStreamId] = useState<string>();
  const saveInProgressRef = useRef(false);

  const onAddFile = async () => {
    await close();
    openModal(ModalTypes.SELECT_VIDEO_MEDIAFILES, {
      openModal,
      prevVideoMediafiles: mediafilesToSave,
      emenuMediafiles: emenuOrder,
      flow,
      title: videoStreamTitle,
      description: videoStreamDescription,
      videoStreamId,
      refetchSteam: refetch,
      isGroupSlave,
    });
  };

  const hasInvalidMediafile = mediafilesToSave.some(
    (it) => !Number.isFinite(it.playDuration) || Number(it.playDuration) <= 0
  );
  const hasAnyMediafile = mediafiles.length > 0 || emenuOrder.length > 0;

  const saveButtonHandler = async () => {
    if (saveInProgressRef.current || !videoStreamTitle || !hasAnyMediafile || hasInvalidMediafile) return;
    saveInProgressRef.current = true;
    setIsSaving(true);
    try {
      const newStreamId = savedStreamId || (await onSave(videoStreamTitle, videoStreamDescription, mediafilesToSave));
      if (!newStreamId) return;
      setSavedStreamId(newStreamId);
      await refetch?.();
      await close();
      navigate(`/video-streams/${newStreamId}`, { replace: true });
    } catch {
      toast.error(TEXTS.VIDEO_STREAM_REFRESH_ERROR);
    } finally {
      saveInProgressRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <ModalScreen
      titlePlacement="left"
      title={modalTitle}
      titleClassName={styles.fixedSection}
      contentClassName={styles.modal}
      containerActionButtonsClassName={styles.fixedSection}
      onClose={close}
      isCloseDisabled={isSaving}
      actionButtons={[
        <Button
          key="button_video_mediafies_next"
          elementId="button_video_mediafies_next"
          type={ButtonType.PRIMARY}
          isLoading={isSaving}
          isDisabled={isSaving || !videoStreamTitle || !hasAnyMediafile || hasInvalidMediafile}
          onClick={saveButtonHandler}>
          {savedStreamId ? TEXTS.RETRY_VIDEO_STREAM_REFRESH : TEXTS.SAVE}
        </Button>,
      ]}>
      <div
        className={styles.listWrapper}
        inert={isSaving || !!savedStreamId}
        data-testid={DATA_TEST_IDS.MEDIAFILES_EDITOR_SCROLL}>
        <DraggableVideoMediafiesList
          itemList={mediafiles}
          emenuMediafiles={emenuOrder}
          updateItems={setMediafiles}
          updateEmenuItems={setEmenuOrder}
          onSetDuration={setMediafilePlayDuration}
          onDraftDurationChange={setMediafileDraftDuration}
          onRemoveMediafile={removeMediafile}
          onCopyMediafile={copyMediafile}
          isBlockedDurationEdit={isGroupSlave}
        />
      </div>
      <div
        className={styles.fields}
        inert={isSaving || !!savedStreamId}>
        <Button
          onClick={onAddFile}
          elementId="button_add_file"
          type={ButtonType.SECONDARY}>
          {TEXTS.ADD_FILE}
        </Button>
        <Input
          elementId="input_videostream_title"
          label={TEXTS.EDIT_VIDEO_STREAM_TITLE_LABEL}
          type={InputType.TEXT}
          value={videoStreamTitle}
          onChange={setVideoStreamTitle}
          required
        />
        <TextArea
          placeholder={TEXTS.EDIT_VIDEO_STREAM_DESCRIPTION_PLACEHOLDER}
          onChange={setVideoStreamDescription}
          label={TEXTS.EDIT_VIDEO_STREAM_DESCRIPTION_LABEL}
          elementId="input_videostream_description"
          rows={2}
          value={videoStreamDescription}
        />
      </div>
    </ModalScreen>
  );
};

export default CreateVideoStreamModal;
